import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  alpha,
} from '@mui/material';
import { ConfirmationNumber as PackageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomerPageSection from '../customer/CustomerPageSection';
import { getCustomerServicePackages } from '../../services/customerServicePackageService';
import { isCustomerServicePackageUsable } from '../../services/servicePackageService';
import { getLocalizedValue } from '../../constants/Languages';
import { CUSTOMER_COLORS, customerOutlinedPrimaryButtonSx } from '../../constants/customerTheme';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { buildCustomerRoute } from '../../utils/storeUtils';

const SUCCESS_COLOR = '#2e7d32';

/**
 * The packages the customer can still book with at this store, with what is left on each.
 * Renders nothing when there are none.
 */
const CustomerProfilePackagesSection = ({ storeId }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    if (!storeId) {
      return undefined;
    }

    let cancelled = false;
    getCustomerServicePackages(storeId)
      .then((result) => {
        if (!cancelled) setPackages((result || []).filter(isCustomerServicePackageUsable));
      })
      .catch(() => {
        if (!cancelled) setPackages([]);
      });

    return () => { cancelled = true; };
  }, [storeId]);

  if (packages.length === 0) {
    return null;
  }

  return (
    <CustomerPageSection sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PackageIcon sx={{ color: CUSTOMER_COLORS.primary }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text }}>
          {t('customer.packages.profile.title')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {packages.map((customerPackage) => (
          <Box
            key={customerPackage.id}
            id={`profile-package-${customerPackage.id}`}
            sx={{
              p: 2,
              borderRadius: 1.5,
              border: `1px solid ${alpha(SUCCESS_COLOR, 0.35)}`,
              bgcolor: alpha(SUCCESS_COLOR, 0.03),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: CUSTOMER_COLORS.text }}>
                {getLocalizedValue(customerPackage.name, i18n.language, undefined, t('customer.packages.card.unnamed'))}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: SUCCESS_COLOR }}>
                {t('customer.packages.usage.remaining', {
                  remaining: customerPackage.remainingQuantity,
                  total: customerPackage.quantity,
                })}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={customerPackage.quantity > 0 ? (customerPackage.usedQuantity / customerPackage.quantity) * 100 : 0}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(SUCCESS_COLOR, 0.15),
                '& .MuiLinearProgress-bar': { bgcolor: SUCCESS_COLOR },
              }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {customerPackage.expiresOn
                  ? t('customer.packages.usage.validUntil', { date: formatCalendarDate(customerPackage.expiresOn) })
                  : t('customer.packages.usage.noExpiry')}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                id={`profile-package-book-${customerPackage.id}`}
                onClick={() => navigate(buildCustomerRoute(storeId, 'booking'), {
                  state: { preSelectedServicePackageId: customerPackage.id },
                })}
                sx={customerOutlinedPrimaryButtonSx}
              >
                {t('customer.packages.actions.bookWithPackage')}
              </Button>
            </Box>
          </Box>
        ))}
      </Box>
    </CustomerPageSection>
  );
};

export default CustomerProfilePackagesSection;
